import React, { useEffect, useRef } from 'react'

import { EN_TO_ES } from './demoTranslations'

type AutoTranslateProps = {
  enabled: boolean
  children: React.ReactNode
}

const ATTRS_TO_TRANSLATE = [
  'placeholder',
  'aria-label',
  'title',
  'alt',
] as const

function translateTextContent(text: string): string {
  const trimmed = text.trim()
  if (!trimmed) return text

  // Case-sensitive EXACT match
  if (EN_TO_ES[trimmed]) {
    return text.replace(trimmed, EN_TO_ES[trimmed])
  }

  return text
}

function translateElementAttributes(el: HTMLElement) {
  // Translate common text attributes
  for (const attr of ATTRS_TO_TRANSLATE) {
    const current = el.getAttribute(attr)
    if (current) {
      const translated = translateTextContent(current)
      if (translated !== current) {
        el.setAttribute(attr, translated)
      }
    }
  }

  // Translate value for inputs / textareas / buttons (e.g., month picker input)
  if (
    el instanceof HTMLInputElement
    || el instanceof HTMLTextAreaElement
    || el instanceof HTMLButtonElement
  ) {
    const current = el.value
    if (current) {
      const translated = translateTextContent(current)
      if (translated !== current) {
        el.value = translated
      }
    }
  }
}

// Walk *entire* subtree and translate everything we care about
function walkAndTranslate(node: Node): void {
  if (node.nodeType === Node.TEXT_NODE) {
    const textNode = node as Text
    const original = textNode.nodeValue ?? ''
    const translated = translateTextContent(original)
    if (translated !== original) {
      textNode.nodeValue = translated
    }
  } else if (node.nodeType === Node.ELEMENT_NODE) {
    const el = node as HTMLElement
    translateElementAttributes(el)
    el.childNodes.forEach(walkAndTranslate)
  } else {
    node.childNodes.forEach(walkAndTranslate)
  }
}

export const AutoTranslate: React.FC<AutoTranslateProps> = ({
  enabled,
  children,
}) => {
  const rootRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!enabled || !rootRef.current) return

    const root = rootRef.current

    const translateAll = () => {
      walkAndTranslate(root)
    }

    // Initial pass
    translateAll()

    const observer = new MutationObserver(_mutations => {
      // Whenever anything changes under root, re-run translation
      translateAll()
    })

    observer.observe(root, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true, // we want any attribute change to trigger a pass
    })

    return () => observer.disconnect()
  }, [enabled])

  return <div ref={rootRef}>{children}</div>
}
